import React, { useState } from 'react';
import { smartFactureAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  CheckCircle, AlertTriangle, XCircle, Loader2, Shield, 
  FileText, ArrowRight, Info, ChevronDown, ChevronUp,
  Send, TestTube, ExternalLink
} from 'lucide-react';

const PPFSimulationModal = ({ 
  isOpen, 
  onClose, 
  invoice,
  onSimulationComplete,
  onTrialError 
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    errors: true,
    warnings: true,
    infos: false
  });

  const runSimulation = async () => {
    if (!invoice?.id) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const response = await smartFactureAPI.simulatePPF(invoice.id);
      setResult(response.data);
      
      if (response.data.status === 'valid') {
        toast({
          title: 'Simulation réussie',
          description: 'Votre facture est prête pour le PPF/PDP',
        });
      }
      
      if (onSimulationComplete) {
        onSimulationComplete(response.data);
      }
    } catch (error) {
      // Check if it's a trial limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.code === 'TRIAL_LIMIT_REACHED') {
        if (onTrialError) {
          onTrialError(error, 'ppf_simulation');
          onClose(); // Close the modal
        }
        return;
      }
      
      toast({
        title: 'Erreur de simulation',
        description: error.response?.data?.detail?.message || error.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReal = async () => {
    if (!invoice?.id) return;
    
    setLoading(true);
    try {
      const response = await smartFactureAPI.submitToPPF(invoice.id);
      
      toast({
        title: response.data.success ? 'Facture préparée' : 'Erreur',
        description: response.data.message,
        variant: response.data.success ? 'default' : 'destructive'
      });
      
      if (response.data.success) {
        onClose();
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec de la soumission',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusDisplay = () => {
    if (!result) return null;
    
    const statusConfig = {
      valid: {
        icon: CheckCircle,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200'
      },
      warning: {
        icon: AlertTriangle,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-amber-200'
      },
      rejected: {
        icon: XCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200'
      }
    };
    
    const config = statusConfig[result.status] || statusConfig.rejected;
    const Icon = config.icon;
    
    return (
      <div className={`p-4 rounded-lg ${config.bg} ${config.border} border`}>
        <div className="flex items-start space-x-3">
          <Icon className={`h-6 w-6 ${config.color} mt-0.5`} />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className={`font-semibold ${config.color}`}>
                {result.status_icon} {result.status_label}
              </h3>
              <Badge variant="outline" className={config.color}>
                Score: {result.compliance_score}%
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {result.status_message}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderValidationItem = (item, index) => {
    const severityConfig = {
      error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
      warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
      info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50' }
    };
    
    const config = severityConfig[item.severity] || severityConfig.info;
    const Icon = config.icon;
    
    return (
      <div key={index} className={`p-3 rounded-lg ${config.bg} mb-2`}>
        <div className="flex items-start space-x-3">
          <Icon className={`h-5 w-5 ${config.color} mt-0.5 flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className={`font-medium ${config.color}`}>{item.message}</p>
            <p className="text-sm text-slate-600 mt-1">{item.explanation}</p>
            <div className="mt-2 p-2 bg-white/50 rounded text-sm">
              <span className="font-medium text-slate-700">Comment corriger : </span>
              <span className="text-slate-600">{item.how_to_fix}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, items, sectionKey, icon) => {
    if (!items || items.length === 0) return null;
    
    const Icon = icon;
    const isExpanded = expandedSections[sectionKey];
    
    return (
      <div className="mt-4">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center justify-between w-full p-2 hover:bg-slate-50 rounded-lg transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Icon className="h-4 w-4 text-slate-500" />
            <span className="font-medium text-slate-700">{title}</span>
            <Badge variant="outline">{items.length}</Badge>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </button>
        {isExpanded && (
          <div className="mt-2 space-y-2">
            {items.map((item, idx) => renderValidationItem(item, idx))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>Simulation PPF/PDP</span>
          </DialogTitle>
          <DialogDescription>
            Testez la conformité de votre facture avant envoi officiel
          </DialogDescription>
        </DialogHeader>

        {/* Invoice Info */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <FileText className="h-5 w-5 text-slate-500" />
            <div>
              <p className="font-medium text-slate-900">{invoice?.invoice_number}</p>
              <p className="text-sm text-slate-500">{invoice?.client_name}</p>
            </div>
          </div>
          <p className="font-semibold text-slate-900">
            {invoice?.grand_total?.toFixed(2)} €
          </p>
        </div>

        {/* Legal Notice */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <p className="text-sm text-blue-800">
              <strong>Mode Simulation :</strong> Cette vérification n'envoie AUCUNE donnée à l'administration fiscale. 
              Elle permet uniquement de valider la conformité avant envoi réel.
            </p>
          </div>
        </div>

        {/* Results */}
        {result ? (
          <div className="space-y-4">
            {getStatusDisplay()}
            
            {/* Next Steps */}
            {result.next_steps && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-700 mb-2">Prochaines étapes :</p>
                <ul className="space-y-1">
                  {result.next_steps.map((step, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-start">
                      <ArrowRight className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Validation Details */}
            {renderSection('Erreurs bloquantes', result.errors, 'errors', XCircle)}
            {renderSection('Avertissements', result.warnings, 'warnings', AlertTriangle)}
            {renderSection('Informations', result.infos, 'infos', Info)}
            
            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setResult(null)}>
                <TestTube className="h-4 w-4 mr-2" />
                Relancer simulation
              </Button>
              {result.is_ready_for_submission && (
                <Button 
                  onClick={handleSubmitReal}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Envoyer officiellement
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Run Simulation Button */
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <TestTube className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Prêt à tester
            </h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto mb-6">
              La simulation vérifie votre facture selon les exigences PPF/PDP 
              sans envoyer de données à l'administration.
            </p>
            <Button 
              onClick={runSimulation}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="run-simulation-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Shield className="h-4 w-4 mr-2" />
              )}
              Lancer la simulation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PPFSimulationModal;
